import React, { useRef, useCallback, useLayoutEffect, useEffect, useState } from 'react';
import { Box, Stack, Paper, Typography } from '@mui/material';

import {
    SimpleSwitch,
    SimpleButton,
    TTSButton,
    ErrorMessage,
    Loading,
    StatusMessage,
} from '@/src/ui';
import { getImageUrl, addImageRef, releaseImageRef } from '@/src/utils';
import { DynamicTextPreview, type QuestionRendererOptions, defaultOptions } from '@/src/lib';
import type { QtiHotspotQuestionDTO, HotspotUserResponse } from '../../models';
import { submitForGrading } from '../../services';

const colorPalette = {
    lightGrey: 'rgba(25,118,210,0.1)',
    regionVisible: 'rgba(25,118,210,0.12)',
    regionStroke: 'rgba(25,118,210,0.6)',
    selectedFill: 'rgba(100,221,23,0.35)',
    selectedStroke: '#64dd17',
    selectedGlow: 'rgba(118,255,3,0.5)',
};

interface HotspotRendererProps {
    question: QtiHotspotQuestionDTO | null;
    handleUpdateUserResponse?: (payload: string, userInitiated: boolean) => void;
    loadedUserResponse?: string;
    options?: QuestionRendererOptions;
    maxBoxWidth?: number;
    maxBoxHeight?: number;
}

export default function HotspotRenderer({
    question,
    handleUpdateUserResponse,
    loadedUserResponse,
    maxBoxWidth,
    maxBoxHeight,
    options = defaultOptions,
}: HotspotRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showLanguageToggle = false,
        showAudioButtons = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [error, setError] = useState<string | null>(null);

    const BOX_MAX_WIDTH = maxBoxWidth ?? 700;
    const BOX_MAX_HEIGHT = maxBoxHeight ?? 600;

    const designW = question?.designCanvas.width ?? 1;
    const designH = question?.designCanvas.height ?? 1;

    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);

    // Internal user response (when no external handler)
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    // ── Background image ───────────────────────────────────────────────
    const [bgUrl, setBgUrl] = useState<string | undefined>(undefined);
    useEffect(() => {
        let prevGuid: string | null = null;
        const url =
            internalLanguage === 'en'
                ? question?.backgroundImageUrl
                : question?.backgroundImageUrl_es;
        if (url?.startsWith('local://')) {
            const guid = url.slice('local://'.length);
            const obj = getImageUrl(guid);
            if (obj) {
                addImageRef(guid);
                setBgUrl(obj);
                prevGuid = guid;
            } else {
                setBgUrl(undefined);
            }
        } else {
            setBgUrl(url || undefined);
        }
        return () => {
            if (prevGuid) releaseImageRef(prevGuid);
        };
    }, [question?.backgroundImageUrl, question?.backgroundImageUrl_es, internalLanguage]);

    // ── Selection state ────────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const userInteractedRef = useRef(false);

    const maxSelections = Number(question?.maxSelections ?? 0);
    const remaining = maxSelections - selectedIds.size;

    // Load saved response
    useEffect(() => {
        if (loadedUserResponse) {
            try {
                const parsed: HotspotUserResponse = JSON.parse(loadedUserResponse);
                if (parsed.selectedRegionIds) {
                    setSelectedIds(new Set(parsed.selectedRegionIds));
                }
            } catch {
                // ignore
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Emit response on change
    useEffect(() => {
        if (selectedIds.size > 0) {
            const response: HotspotUserResponse = {
                selectedRegionIds: Array.from(selectedIds),
            };
            handleUpdateUserResponse!(JSON.stringify(response), userInteractedRef.current);
        } else {
            handleUpdateUserResponse!('', userInteractedRef.current);
        }
        userInteractedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    // ── Toggle region selection ────────────────────────────────────────
    const toggleRegion = useCallback(
        (regionId: string) => {
            if (disabled) return;

            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(regionId)) {
                    // Deselect
                    next.delete(regionId);
                } else {
                    // Select — check max
                    if (next.size >= maxSelections) return prev;
                    next.add(regionId);
                }
                return next;
            });
            userInteractedRef.current = true;
        },
        [disabled, maxSelections]
    );

    // ── Responsive scaling ─────────────────────────────────────────────
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            setContainerWidth(Math.floor(entry.contentRect.width));
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const fitByWidth = containerWidth > 0 ? containerWidth / designW : 1;
    const fitByHeight = BOX_MAX_HEIGHT / designH;
    const renderScale = Math.min(1, fitByWidth, fitByHeight);

    const renderW = Math.round(designW * renderScale);
    const renderH = Math.round(designH * renderScale);

    // ── Early return ───────────────────────────────────────────────────
    if (!question) return 'Unable to load question';

    const regions = question.regions ?? [];

    return (
        <Stack spacing={2}>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}

            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}

            <Stack direction="column" spacing={2}>
                <Paper elevation={1} sx={{ p: 1 }}>
                    <Box ref={containerRef} sx={{ width: '100%', maxWidth: `${BOX_MAX_WIDTH}px` }}>
                        <Box
                            sx={{
                                position: 'relative',
                                width: `${renderW}px`,
                                height: `${renderH}px`,
                                backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat',
                                border: `1px solid ${colorPalette.lightGrey}`,
                                overflow: 'visible',
                                userSelect: 'none',
                            }}
                        >
                            <svg
                                width={renderW}
                                height={renderH}
                                style={{ display: 'block', position: 'relative', zIndex: 1 }}
                            >
                                {/* Pulse animation for selected regions */}
                                <defs>
                                    <style>{`
                                        @keyframes hotspot-region-pulse {
                                            0% { stroke-opacity: 1; }
                                            50% { stroke-opacity: 0.4; }
                                            100% { stroke-opacity: 1; }
                                        }
                                        .hotspot-selected-region {
                                            animation: hotspot-region-pulse 1.8s ease-in-out infinite;
                                        }
                                    `}</style>
                                </defs>

                                {/* Render regions */}
                                {regions
                                    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                                    .map((region) => {
                                        const isSelected = selectedIds.has(region.id);

                                        const g =
                                            internalLanguage === 'en'
                                                ? region.geometry
                                                : (region.geometry_es ?? region.geometry);

                                        // Selected: green, Visible+unselected: blue hint, Hidden+unselected: transparent but clickable
                                        let fill: string;
                                        let stroke: string;
                                        let strokeWidth: number;
                                        if (isSelected) {
                                            fill = colorPalette.selectedFill;
                                            stroke = colorPalette.selectedStroke;
                                            strokeWidth = 3;
                                        } else if (region.visible) {
                                            fill = colorPalette.regionVisible;
                                            stroke = colorPalette.regionStroke;
                                            strokeWidth = 2;
                                        } else {
                                            fill = 'transparent';
                                            stroke = 'transparent';
                                            strokeWidth = 0;
                                        }

                                        // Cursor logic
                                        let cursor: string = 'pointer';
                                        if (disabled) {
                                            cursor = 'not-allowed';
                                        } else if (!isSelected && remaining <= 0) {
                                            cursor = 'not-allowed';
                                        }

                                        const handleClick = (e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            toggleRegion(region.id);
                                        };

                                        if (g.type === 'rect') {
                                            const x = (g.x ?? 0) * designW * renderScale;
                                            const y = (g.y ?? 0) * designH * renderScale;
                                            const w = (g.width ?? 0) * designW * renderScale;
                                            const h = (g.height ?? 0) * designH * renderScale;
                                            const cr = Math.max(
                                                0,
                                                (g.cornerRadius ?? 0) * Math.min(w, h)
                                            );
                                            return (
                                                <g
                                                    key={region.id}
                                                    onClick={handleClick}
                                                    style={{ cursor }}
                                                >
                                                    {/* Glow behind selected regions */}
                                                    {isSelected && (
                                                        <rect
                                                            className="hotspot-selected-region"
                                                            x={x - 3}
                                                            y={y - 3}
                                                            width={w + 6}
                                                            height={h + 6}
                                                            rx={cr + 2}
                                                            ry={cr + 2}
                                                            fill="none"
                                                            stroke={colorPalette.selectedGlow}
                                                            strokeWidth={4}
                                                        />
                                                    )}
                                                    <rect
                                                        x={x}
                                                        y={y}
                                                        width={w}
                                                        height={h}
                                                        rx={cr}
                                                        ry={cr}
                                                        fill={fill}
                                                        stroke={stroke}
                                                        strokeWidth={strokeWidth}
                                                    />
                                                </g>
                                            );
                                        } else if (g.type === 'circle') {
                                            const cx = (g.cx ?? 0) * designW * renderScale;
                                            const cy = (g.cy ?? 0) * designH * renderScale;
                                            const r = (g.r ?? 0) * designW * renderScale;
                                            return (
                                                <g
                                                    key={region.id}
                                                    onClick={handleClick}
                                                    style={{ cursor }}
                                                >
                                                    {/* Glow behind selected regions */}
                                                    {isSelected && (
                                                        <circle
                                                            className="hotspot-selected-region"
                                                            cx={cx}
                                                            cy={cy}
                                                            r={r + 3}
                                                            fill="none"
                                                            stroke={colorPalette.selectedGlow}
                                                            strokeWidth={4}
                                                        />
                                                    )}
                                                    <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={r}
                                                        fill={fill}
                                                        stroke={stroke}
                                                        strokeWidth={strokeWidth}
                                                    />
                                                </g>
                                            );
                                        }
                                        return null;
                                    })}
                            </svg>
                        </Box>
                    </Box>
                </Paper>

                {/* Selection counter */}
                <Paper elevation={1} sx={{ p: 1 }}>
                    <div>Hint: Click to select and unselect items.</div>
                    <Typography variant="body2">
                        {selectedIds.size === maxSelections && !disabled && (
                            <>
                                <br />
                                <ErrorMessage error="Maximum number of selections has been reached." />
                            </>
                        )}
                    </Typography>
                    {!disabled && selectedIds.size > 0 && (
                        <>
                            <br />
                            <SimpleButton
                                color="error"
                                variant="outlined"
                                className="mt-1"
                                onClick={() => {
                                    setSelectedIds(new Set());
                                    userInteractedRef.current = true;
                                }}
                            >
                                Remove All Selections
                            </SimpleButton>
                        </>
                    )}
                </Paper>
            </Stack>

            {showScoring && <HotspotScoring question={question} userResponse={userResponse} />}
            <ErrorMessage error={error} />
        </Stack>
    );
}

interface HotspotScoringProps {
    question: QtiHotspotQuestionDTO;
    userResponse: string;
}

function HotspotScoring({ question, userResponse }: HotspotScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setAnswerChecked(false);
        setMessage('');
    }, [userResponse]);

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect, message } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setMessage(message ?? '');
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    const parsedResponse: HotspotUserResponse | null = userResponse
        ? JSON.parse(userResponse)
        : null;
    if (!parsedResponse) return null;

    const selectedIds = new Set(parsedResponse.selectedRegionIds ?? []);

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={!selectedIds.size || answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                <Loading visible={loading} message="Checking answer..." />
            </div>

            <ErrorMessage error={error} />
            <StatusMessage
                color={isCorrect ? 'green' : 'red'}
                visible={answerChecked && selectedIds.size > 0}
                status={isCorrect ? 'Correct!' : `Incorrect. ${message}`}
            />
        </div>
    );
}
